import React, { useEffect, useState, useCallback } from 'react';
import { Card, Tabs, Empty, Spin, Tag, Typography, List, Button, Space } from 'antd';
import { ClockCircleOutlined, BellOutlined, CalendarOutlined } from '@ant-design/icons';
import apiService, { Lead } from '../../services/api';
import NotesPanel from '../NotesPanel';
import { workspaceSurface, workspaceCardHead, workspaceCardBody } from './workspaceTheme';

const { Text } = Typography;

interface ActivityRailProps {
  lead: Lead;
}

const ActivityRail: React.FC<ActivityRailProps> = ({ lead }) => {
  const [notes, setNotes] = useState<any[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const r = await apiService.getLeadNotes(lead.id);
      setNotes(r.notes || []);
    } catch {
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [lead.id]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await apiService.getAssignmentHistory(lead.id);
      setHistory(r || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    loadNotes();
    loadHistory();
  }, [loadNotes, loadHistory]);

  return (
    <Card
      style={{ ...workspaceSurface, width: '100%' }}
      headStyle={workspaceCardHead}
      bodyStyle={{ ...workspaceCardBody, padding: 0 }}
      title="Atividade & Notas"
    >
      <Tabs
        defaultActiveKey="notes"
        tabBarStyle={{
          paddingLeft: 8,
          paddingRight: 8,
          marginBottom: 0,
          background: 'linear-gradient(180deg, #f4f6fa 0%, #e8ecf4 100%)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
        }}
        style={{ padding: '0 8px' }}
        items={[
          {
            key: 'notes',
            label: 'Notas',
            children: notesLoading ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <Spin />
              </div>
            ) : (
              <div style={{ padding: '0 12px 16px' }}>
                <NotesPanel
                  leadId={lead.id}
                  notes={notes}
                  onNotesChange={(updated) => setNotes(updated)}
                />
              </div>
            ),
          },
          {
            key: 'timeline',
            label: 'Timeline',
            children: (
              <div style={{ padding: '12px 8px 16px' }}>
                <Card
                  size="small"
                  style={{
                    marginBottom: 12,
                    background: '#ffffff',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: 10,
                  }}
                >
                  <Space direction="vertical" size={4}>
                    <Text strong>
                      <CalendarOutlined /> Próximo follow-up
                    </Text>
                    <Text>
                      {lead.next_follow_up
                        ? new Date(lead.next_follow_up).toLocaleDateString('pt-PT')
                        : 'Sem follow-up agendado.'}
                    </Text>
                    <Text strong>
                      <ClockCircleOutlined /> Último contacto
                    </Text>
                    <Text>
                      {lead.last_contact_date
                        ? new Date(lead.last_contact_date).toLocaleDateString('pt-PT')
                        : 'Ainda não registado.'}
                    </Text>
                    {lead.installation_date && (
                      <>
                        <Text strong>
                          <BellOutlined /> Instalação
                        </Text>
                        <Text>
                          {new Date(lead.installation_date).toLocaleDateString('pt-PT')}
                        </Text>
                      </>
                    )}
                  </Space>
                </Card>

                <Text strong style={{ marginLeft: 4 }}>
                  Histórico de Atribuições
                </Text>
                {historyLoading ? (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                  </div>
                ) : history.length === 0 ? (
                  <Empty
                    description="Sem histórico"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <List
                    size="small"
                    dataSource={history}
                    renderItem={(h: any) => (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            <Text>
                              {h.assigned_to_name || 'Utilizador'}{' '}
                              <Tag color="blue">{h.assignment_type || 'atribuído'}</Tag>
                            </Text>
                          }
                          description={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {h.created_at
                                ? new Date(h.created_at).toLocaleString('pt-PT')
                                : ''}
                              {h.assigned_by_name ? ` — por ${h.assigned_by_name}` : ''}
                            </Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default ActivityRail;
